/**
 * A soft fog band lying on the sea and in the town's low ground: strongest around sunrise ±2 h, scaled by humidity
 * and low visibility. Its colour follows the horizon sky radiance, so it always matches the sky.
 */
import * as THREE from 'three';
import type { WorldState } from '../../world/state/types';
import { EYE, SEA_LEVEL_Y } from '../composition';

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

/** 0..1 opacity of the morning fog band for a state (pure, tested). */
export function morningFogAmount(state: WorldState): number {
  const h = state.time.local.hour + state.time.local.minute / 60;
  const aroundDawn = clamp01(1 - Math.abs(h - 6) / 2.5);
  const humid = clamp01((state.weather.humidity - 0.7) / 0.25);
  const murky = clamp01((8000 - state.weather.visibilityM) / 7000);
  return clamp01(0.15 + aroundDawn * 0.6 * humid + murky * 0.7) * (state.weather.visibilityM < 1000 ? 1 : 0.85);
}

export function buildMorningFog(): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.CircleGeometry(9000, 64),
    new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0, depthWrite: false }),
  );
  mesh.name = 'morning_fog';
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(EYE[0], SEA_LEVEL_Y + 6, EYE[2] - 3000);
  mesh.renderOrder = 5;
  return mesh;
}

/**
 * hazeColor: sky radiance just above the horizon (from the sky LightProbe). An unlit white would be radiance 1 and
 * blow out under night exposure (~×17), whitening the whole sea.
 */
export function updateMorningFog(mesh: THREE.Mesh, state: WorldState, hazeColor: THREE.Color): void {
  const m = mesh.material as THREE.MeshBasicMaterial;
  m.opacity = 0.55 * morningFogAmount(state);
  m.color.copy(hazeColor);
}
